import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { RiskBadge, STAGE_LABELS } from "./Badges";
import { PhotoUpload } from "./PhotoUpload";
import { WorkOrderDetail } from "./WorkOrderDetail";

const RISK_HELP = {
  high: "Fire system faults, blocked fire exits, emergency lighting failures, electrical hazards - Supervisor alerted now, then H&S Manager and Site Manager every 4 hours if unresolved",
  medium: "Damaged signage, minor building defects - Supervisor alerted, then escalated every 24 hours if unresolved",
  low: "Cosmetic issues - normal maintenance planning",
};

export const NewIssueDialog = ({ open, onOpenChange, onCreated, preset, plants, codes }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ risk: "medium", type: "reactive", photos: [], ...(preset || {}) });
  }, [open, preset]);
  const submit = async () => {
    try {
      const r = await api.post("/workorders", f);
      toast.success(`${r.data.ref} raised${r.data.risk === "high" ? " - managers alerted" : ""}`);
      window.dispatchEvent(new Event("wo-changed"));
      onOpenChange(false);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-700 bg-ink-2 sm:max-w-xl" data-testid="new-issue-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{preset?.category === "ASRS" ? "Report plant fault" : "Report issue / defect"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className="field" placeholder="What is the issue? *" value={f.title || ""} onChange={set("title")} data-testid="issue-title" />
          <textarea className="field h-24 py-2" placeholder="Description" value={f.description || ""} onChange={set("description")} data-testid="issue-description" />
          {plants && (
            <select className="field" value={f.asset_id || ""} data-testid="issue-plant"
              onChange={(e) => { const p = plants.find((x) => x.id === e.target.value); setF({ ...f, asset_id: p?.id, asset_name: p?.asset_code, location: p?.asset_code || f.location }); }}>
              <option value="">Plant (crane, infeed, outfeed...)</option>
              {plants.map((p) => <option key={p.id} value={p.id}>{p.asset_code} - {p.category}</option>)}
            </select>
          )}
          {codes && (
            <select className="field" value={f.fault_code || ""} data-testid="issue-fault-code"
              onChange={(e) => { const c = codes.find((x) => x.code === e.target.value); setF({ ...f, fault_code: c?.code, title: f.title || (c ? `${c.code} ${c.label}` : f.title) }); }}>
              <option value="">Fault code (optional)</option>
              {codes.map((c) => <option key={c.code} value={c.code}>{c.code} - {c.label}</option>)}
            </select>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="field" placeholder="Location" value={f.location || ""} onChange={set("location")} data-testid="issue-location" />
            <select className="field" value={f.type} onChange={set("type")} data-testid="issue-type">
              <option value="reactive">Reactive request</option><option value="defect">Defect</option><option value="ppm">PPM</option>
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {["high", "medium", "low"].map((r) => (
              <button key={r} type="button" onClick={() => setF({ ...f, risk: r })} data-testid={`issue-risk-${r}`}
                className={`tap rounded-md border text-sm font-semibold uppercase ${f.risk === r ? { high: "border-red-500 bg-red-500/20 text-red-200", medium: "border-amber-500 bg-amber-500/20 text-amber-200", low: "border-emerald-500 bg-emerald-500/20 text-emerald-200" }[r] : "border-slate-700 text-slate-400"}`}>{r}</button>
            ))}
          </div>
          <p className="text-xs text-slate-400" data-testid="issue-risk-help">{RISK_HELP[f.risk]}</p>
          <PhotoUpload value={f.photos || []} onChange={(photos) => setF({ ...f, photos })} testid="issue-photos" />
          <Button onClick={submit} disabled={!f.title} className="tap w-full bg-rose-600 hover:bg-rose-700" data-testid="issue-submit-btn">Raise issue</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const WorkOrders = ({ category }) => {
  const { can, user } = useAuth();
  const mod = category === "ASRS" ? "asrs" : "maintenance";
  const canWrite = can(mod, "check");
  const isManager = can(mod, "manage");
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [creating, setCreating] = useState(false);
  const [plants, setPlants] = useState(null);

  const load = useCallback(() => api.get("/workorders", { params: { status: status === "all" ? undefined : status, category } }).then((r) => setRows(r.data)), [status, category]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (category === "ASRS") {
      api.get("/assets", { params: { kind: "asrs_plant" } }).then((r) => setPlants(r.data));
      api.get("/asrs/fault-codes").then((r) => setCodes(r.data));
    }
  }, [category]);
  const [codes, setCodes] = useState(null);

  const ppm = async () => {
    try {
      const r = await api.post(category === "ASRS" ? "/asrs/ppm/generate" : "/ppm/generate");
      toast.success(r.data.created.length ? `Created ${r.data.created.join(", ")}` : "No PPM due in the next 14 days");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="space-y-4" data-testid="workorders-panel">
      <div className="flex flex-wrap gap-2">
        {["open", "closed", "all"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`tap rounded-md px-4 text-sm capitalize ${status === s ? "bg-rose-600 text-white" : "bg-slate-900 text-slate-400"}`} data-testid={`wo-filter-${s}`}>{s}</button>
        ))}
        <div className="flex-1" />
        {isManager && <Button variant="outline" className="tap border-slate-700 bg-transparent" onClick={ppm} data-testid="generate-ppm-btn"><CalendarClock className="mr-1 h-4 w-4" />Generate PPM</Button>}
        {canWrite && user.role !== "contractor" && <Button className="tap bg-rose-600 hover:bg-rose-700" onClick={() => setCreating(true)} data-testid="new-issue-btn"><Plus className="mr-1 h-4 w-4" />{category === "ASRS" ? "Report plant fault" : "Report issue"}</Button>}
      </div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((w) => (
          <button key={w.id} onClick={() => setParams({ wo: w.id })} className="panel p-4 text-left transition-colors hover:border-slate-600" data-testid={`wo-card-${w.ref}`}>
            <div className="mb-2 flex items-center justify-between"><span className="font-mono text-xs text-cyan-300">{w.ref}</span><RiskBadge risk={w.risk} /></div>
            <p className="font-semibold text-slate-100">{w.title}</p>
            <p className="mt-1 text-xs text-slate-400">{w.location || "-"} - {w.type}</p>
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="rounded bg-slate-800 px-2 py-1 text-slate-200">{STAGE_LABELS[w.stage]}</span>
              <span className={w.stage !== "closed" && new Date(w.due_date) < new Date(new Date().toDateString()) ? "text-red-400" : "text-slate-400"}>Due {fmtDate(w.due_date)}</span>
            </div>
          </button>
        ))}
        {!rows.length && <p className="panel col-span-full px-4 py-10 text-center text-sm text-slate-500">No work orders.</p>}
      </div>
      <NewIssueDialog open={creating} onOpenChange={setCreating} onCreated={load} preset={category ? { category } : undefined} plants={plants} codes={codes} />
      <WorkOrderDetail id={params.get("wo")} onClose={() => setParams({})} onChange={load} />
    </div>
  );
};
